/**
 * CuisinePreferencesModal.js
 * 
 * Modal to collect user's cuisine preferences for improved food recognition.
 * Shows as a one-time prompt for existing users or during first photo analysis.
 */

import React, { useState, useEffect } from 'react';
import { X, Check, ChefHat } from 'lucide-react';

const CUISINE_OPTIONS = [
  { id: "North American", label: "North American", desc: "US, Canadian" },
  { id: "Latin American", label: "Latin American", desc: "Mexican, Caribbean, South American" },
  { id: "Western European", label: "Western European", desc: "French, German, British, Spanish" },
  { id: "Italian", label: "Italian", desc: "Pasta, Pizza, Mediterranean Italian" },
  { id: "Eastern European", label: "Eastern European", desc: "Russian, Polish, Ukrainian, Hungarian" },
  { id: "Nordic", label: "Nordic", desc: "Finnish, Norwegian, Swedish, Danish" },
  { id: "Mediterranean & Middle Eastern", label: "Mediterranean & Middle Eastern", desc: "Greek, Turkish, Lebanese, Arabic, Persian" },
  { id: "South Asian", label: "South Asian", desc: "Indian, Pakistani, Bangladeshi, Sri Lankan" },
  { id: "East Asian", label: "East Asian", desc: "Chinese, Japanese, Korean" },
  { id: "Southeast Asian", label: "Southeast Asian", desc: "Thai, Vietnamese, Filipino, Indonesian" },
  { id: "African", label: "African", desc: "West African, East African, Ethiopian" },
  { id: "Other", label: "Other", desc: "Other cuisines not listed" }
];

const CuisinePreferencesModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  userId,
  BACKEND_URL,
  initialPreferences = [],
  isFirstTime = true 
}) => {
  const [selectedCuisines, setSelectedCuisines] = useState(initialPreferences);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (initialPreferences.length > 0) {
      setSelectedCuisines(initialPreferences);
    }
  }, [initialPreferences]);

  const toggleCuisine = (cuisineId) => {
    setSelectedCuisines(prev => {
      if (prev.includes(cuisineId)) {
        return prev.filter(c => c !== cuisineId);
      } else {
        // Limit to 5 selections
        if (prev.length >= 5) {
          return prev;
        }
        return [...prev, cuisineId];
      }
    });
  };

  const handleSave = async () => {
    if (selectedCuisines.length === 0) {
      setError('Please select at least one cuisine');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/users/${userId}/cuisine-preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cuisine_preferences: selectedCuisines })
      });

      if (!response.ok) {
        throw new Error('Failed to save preferences');
      }

      const data = await response.json();
      
      // Mark as shown in localStorage
      localStorage.setItem(`cuisine_prefs_shown_${userId}`, 'true');
      
      if (onSave) {
        onSave(data.cuisine_preferences);
      }
      onClose();
    } catch (err) {
      console.error('Error saving cuisine preferences:', err);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    // Mark as shown but not set
    localStorage.setItem(`cuisine_prefs_shown_${userId}`, 'skipped');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      data-testid="cuisine-preferences-modal"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}
    >
      <div style={{
        background: 'var(--bg-card, #1a1a2e)',
        borderRadius: '16px',
        maxWidth: '480px',
        width: '100%',
        maxHeight: '85vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 20px 16px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <ChefHat size={22} color="white" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>
                {isFirstTime ? 'Improve Food Recognition' : 'Cuisine Preferences'}
              </h2>
              <p style={{ margin: '2px 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
                Select up to 5 cuisines you eat most often
              </p>
            </div>
          </div>
          <button
            onClick={handleSkip}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Info Banner */}
        {isFirstTime && (
          <div style={{
            margin: '16px 20px 0',
            padding: '12px 14px',
            background: 'rgba(102, 126, 234, 0.1)',
            borderRadius: '10px',
            border: '1px solid rgba(102, 126, 234, 0.2)'
          }}>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
              This helps our AI identify your meals more accurately. For example, if you eat South Asian food, 
              a rice dish is more likely to be biryani than paella.
            </p>
          </div>
        )}

        {/* Cuisine Grid */}
        <div style={{
          padding: '16px 20px',
          overflowY: 'auto',
          flex: 1
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '10px'
          }}>
            {CUISINE_OPTIONS.map(cuisine => {
              const isSelected = selectedCuisines.includes(cuisine.id);
              const isDisabled = !isSelected && selectedCuisines.length >= 5;
              
              return (
                <button
                  key={cuisine.id}
                  onClick={() => !isDisabled && toggleCuisine(cuisine.id)}
                  data-testid={`cuisine-option-${cuisine.id.toLowerCase().replace(/\s+/g, '-')}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    padding: '12px 14px',
                    background: isSelected 
                      ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)'
                      : 'rgba(255,255,255,0.03)',
                    border: isSelected 
                      ? '2px solid #667eea' 
                      : '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '10px',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    opacity: isDisabled ? 0.5 : 1,
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    textAlign: 'left'
                  }}
                >
                  {isSelected && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      background: '#667eea',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Check size={12} color="white" />
                    </div>
                  )}
                  <span style={{ 
                    fontSize: '14px', 
                    fontWeight: '500', 
                    color: isSelected ? 'var(--text-primary)' : 'var(--text-secondary)',
                    paddingRight: isSelected ? '24px' : '0'
                  }}>
                    {cuisine.label}
                  </span>
                  <span style={{ 
                    fontSize: '11px', 
                    color: 'var(--text-tertiary, #666)',
                    marginTop: '2px',
                    lineHeight: '1.3'
                  }}>
                    {cuisine.desc}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            margin: '0 20px',
            padding: '10px 14px',
            background: 'rgba(239, 68, 68, 0.1)',
            borderRadius: '8px',
            border: '1px solid rgba(239, 68, 68, 0.3)'
          }}>
            <p style={{ margin: 0, fontSize: '13px', color: '#ef4444' }}>{error}</p>
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '16px 20px',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          gap: '12px'
        }}>
          <button
            onClick={handleSkip}
            style={{
              flex: 1,
              padding: '14px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '10px',
              color: 'var(--text-secondary)',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            {isFirstTime ? 'Maybe Later' : 'Cancel'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || selectedCuisines.length === 0}
            data-testid="save-cuisine-preferences"
            style={{
              flex: 2,
              padding: '14px',
              background: selectedCuisines.length > 0 
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '10px',
              color: 'white',
              fontSize: '14px',
              fontWeight: '600',
              cursor: selectedCuisines.length > 0 ? 'pointer' : 'not-allowed',
              opacity: saving ? 0.7 : 1
            }}
          >
            {saving ? 'Saving...' : `Save Preferences (${selectedCuisines.length}/5)`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CuisinePreferencesModal;
